import { key } from './geometry';
import type { ObstacleMap, Point, Puzzle, Terrain } from './types';

/**
 * The five properties of the span rule the training yard isolates (GDD §4.6).
 *
 * Each family names something the daily rewards but never explains. A drill
 * belongs to exactly one of them: a layout that teaches two things teaches
 * neither, which is the whole reason these are authored rather than generated.
 */
export type DrillFamily =
  'diagonal-tax' | 'triangle-trap' | 'exact-spacing' | 'junction' | 'lake-economics';

/** One hand-authored lesson: a fixed map, and the one thing it is about. */
export interface Drill {
  /**
   * Stable key, forever. A completed drill is persisted against this id (a
   * `DrillResult`), so an id is never renumbered, and never reused for a
   * layout that isn't this one.
   */
  id: string;
  /** Player-facing name. */
  name: string;
  family: DrillFamily;
  /** The lesson, stated as a fact of the physics, in one line. */
  teaches: string;
  /**
   * The layout. `seedTowers` is the naive read the drill exists to beat: it
   * lights every house and is measurably worse than the reference, in exactly
   * the way the family names.
   */
  puzzle: Puzzle;
}

/** Shorthand, so a hand-placed layout reads as a list of coordinates. */
const at = (r: number, c: number): Point => ({ r, c });

/** Paint an inclusive rectangle of terrain into an obstacle map. */
function rect(
  map: ObstacleMap,
  terrain: Terrain,
  r0: number,
  c0: number,
  r1: number,
  c1: number,
): ObstacleMap {
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) map[key(at(r, c))] = terrain;
  }
  return map;
}

/**
 * Buttes and short ridges, clear of the lesson.
 *
 * Not decoration: an empty 9x9 field reads as a canvas rather than a map
 * (STYLEGUIDE §4), and every cell they take out is a cell the reference search
 * never has to try. Terrain that moved a drill's answer would break that
 * drill's own test, which is how these stay honest.
 */
function buttes(list: Point[]): ObstacleMap {
  const map: ObstacleMap = {};
  for (const p of list) map[key(p)] = 'mtn';
  return map;
}

/**
 * The training yard, easiest first.
 *
 * Families run in the order of the GDD §4.6 table, which is also roughly
 * increasing difficulty: the diagonal tax is one span of arithmetic, lake
 * economics is a whole route. Inside a family, the drill whose gap is easiest
 * to see comes first.
 */
export const DRILLS: readonly Drill[] = [
  {
    id: 'diagonal-tax-weave',
    name: 'The weave',
    family: 'diagonal-tax',
    teaches: 'Reach counts cells, wire counts distance. A diagonal span costs 41% more.',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 4), at(6, 4), at(4, 8)],
      obstacles: buttes([at(0, 1), at(0, 2), at(1, 1), at(8, 6), at(8, 7), at(7, 7)]),
      par: 3,
      seedTowers: [at(2, 2), at(4, 4), at(2, 6)],
      archetype: 'radial',
    },
  },
  {
    id: 'diagonal-tax-long-climb',
    name: 'The long climb',
    family: 'diagonal-tax',
    teaches: 'Both lines light every house. The straight one strings less wire.',
    puzzle: {
      plant: at(8, 4),
      houses: [at(4, 2), at(4, 6), at(0, 4)],
      obstacles: buttes([at(8, 0), at(8, 1), at(7, 0), at(0, 7), at(0, 8), at(1, 8)]),
      par: 3,
      seedTowers: [at(6, 2), at(4, 4), at(2, 2)],
      archetype: 'radial',
    },
  },
  {
    id: 'triangle-trap-fan',
    name: 'The fan',
    family: 'triangle-trap',
    teaches:
      'Three towers within span of one another string three wires. Two in a line string two.',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 4), at(6, 4), at(4, 6)],
      obstacles: buttes([at(0, 7), at(0, 8), at(1, 8), at(8, 7), at(8, 8), at(7, 8)]),
      par: 3,
      seedTowers: [at(3, 2), at(5, 2), at(4, 4)],
      archetype: 'radial',
    },
  },
  {
    id: 'triangle-trap-huddle',
    name: 'The huddle',
    family: 'triangle-trap',
    teaches: 'Towers packed close string a wire to every neighbour in reach, and pay for each one.',
    puzzle: {
      plant: at(8, 4),
      houses: [at(4, 3), at(4, 5), at(1, 4)],
      obstacles: buttes([at(0, 0), at(0, 1), at(1, 0), at(0, 8), at(1, 8), at(8, 8)]),
      par: 4,
      seedTowers: [at(6, 3), at(6, 5), at(5, 4), at(3, 4)],
      archetype: 'radial',
    },
  },
  {
    id: 'triangle-trap-delta',
    name: 'The delta',
    family: 'triangle-trap',
    teaches: 'A tower each side of a house also pays for the span across it.',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 6), at(6, 6), at(4, 7)],
      obstacles: buttes([at(0, 0), at(0, 1), at(1, 0), at(8, 0), at(8, 1), at(7, 0)]),
      par: 4,
      seedTowers: [at(4, 2), at(4, 4), at(3, 6), at(5, 6)],
      archetype: 'radial',
    },
  },
  {
    id: 'exact-spacing-footings',
    name: 'Footings',
    family: 'exact-spacing',
    teaches: 'Set two apart, a tower wires to one neighbour. Set closer, it wires to two.',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 8), at(6, 8), at(4, 7)],
      obstacles: rect(rect({}, 'water', 0, 3, 8, 3), 'water', 0, 5, 8, 5),
      par: 4,
      seedTowers: [at(4, 1), at(4, 2), at(4, 4), at(4, 6)],
      archetype: 'delta',
    },
  },
  {
    id: 'exact-spacing-island',
    name: 'The island',
    family: 'exact-spacing',
    teaches:
      'One row of dry ground midstream. Spend the towers reaching it, not creeping up to it.',
    puzzle: {
      plant: at(0, 4),
      houses: [at(8, 2), at(8, 6), at(7, 4)],
      obstacles: rect(rect({}, 'water', 3, 0, 3, 8), 'water', 5, 0, 5, 8),
      par: 4,
      seedTowers: [at(1, 4), at(2, 4), at(4, 4), at(6, 4)],
      archetype: 'delta',
    },
  },
  {
    id: 'junction-neck',
    name: 'One pole, three drops',
    family: 'junction',
    teaches: 'A tower within span of all three houses feeds them from one place.',
    puzzle: {
      plant: at(4, 0),
      houses: [at(3, 6), at(5, 6), at(4, 8)],
      obstacles: buttes([at(4, 7), at(0, 0), at(0, 1), at(8, 0), at(8, 1)]),
      par: 4,
      seedTowers: [at(4, 2), at(4, 4), at(2, 6), at(6, 6)],
      archetype: 'radial',
    },
  },
  {
    id: 'junction-column',
    name: 'The branch point',
    family: 'junction',
    teaches: 'Where the line splits sets the wire. Split once, and late.',
    puzzle: {
      plant: at(8, 4),
      houses: [at(2, 3), at(2, 5), at(0, 4)],
      obstacles: buttes([at(1, 4), at(8, 0), at(7, 0), at(8, 8), at(7, 8)]),
      par: 4,
      seedTowers: [at(6, 4), at(4, 4), at(3, 2), at(1, 3)],
      archetype: 'radial',
    },
  },
  {
    id: 'junction-approach',
    name: 'The far pole',
    family: 'junction',
    teaches: 'A branch made early is carried the whole way. Take the line to the houses first.',
    puzzle: {
      plant: at(7, 8),
      houses: [at(3, 2), at(5, 2), at(4, 0)],
      obstacles: buttes([at(4, 1), at(0, 0), at(0, 1), at(0, 8), at(1, 8)]),
      par: 5,
      seedTowers: [at(6, 6), at(5, 4), at(3, 3), at(7, 3), at(5, 1)],
      archetype: 'radial',
    },
  },
  {
    id: 'lake-narrows',
    name: 'The narrows',
    family: 'lake-economics',
    teaches: 'Water two cells wide is out of span. One cell wide is a crossing.',
    puzzle: {
      plant: at(1, 1),
      houses: [at(1, 7), at(3, 7), at(5, 7)],
      obstacles: rect(rect({}, 'water', 0, 4, 8, 4), 'water', 2, 5, 4, 5),
      par: 5,
      seedTowers: [at(3, 2), at(5, 3), at(6, 5), at(4, 6), at(2, 6)],
      archetype: 'river',
    },
  },
  {
    id: 'lake-round-the-end',
    name: 'Round the end',
    family: 'lake-economics',
    teaches: 'The water ends before the map does. Walking round it costs less than crossing it.',
    puzzle: {
      plant: at(7, 1),
      houses: [at(3, 7), at(5, 7), at(7, 7)],
      obstacles: rect(rect({}, 'water', 0, 4, 5, 4), 'water', 2, 5, 4, 5),
      par: 5,
      seedTowers: [at(5, 1), at(5, 3), at(5, 5), at(4, 7), at(6, 7)],
      archetype: 'river',
    },
  },
];

/** Look a drill up by its persistence key. `undefined` for an id we never had. */
export function drillById(id: string): Drill | undefined {
  return DRILLS.find((d) => d.id === id);
}

/**
 * The seed a drill's reference search runs on.
 *
 * A drill has no grid number to seed from, and it reveals its reference the
 * moment it is solved (GDD §4.5), so the layout it reveals has to be the same
 * one on every device. The id is the only thing about a drill that is stable
 * forever, so the seed is derived from it: FNV-1a, 32-bit, which is enough
 * spread for a dozen fixed maps and identical on every engine.
 */
export function drillSeed(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}
